import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PATHS, AUTH_TOKEN_KEY, B2C_WEBSITE_URL } from '@/constants';
import { useLogoutMutation, useMeQuery } from '@/graphql';

import { AuthenticatedGuardContext, type AuthenticatedGuardContextType } from './context';

export const AuthenticatedGuardProvider: React.FC<{ children: JSX.Element | null }> = ({ children }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [cookies, , removeCookie] = useCookies([AUTH_TOKEN_KEY]);
  const [isPreferredLanguageChanged, setIsPreferredLanguageChanged] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const meResult = useMeQuery(
    {},
    {
      onError: () => {
        removeCookie(AUTH_TOKEN_KEY, { domain: AUTH_TOKEN_KEY, path: '/' });
        navigate(PATHS.LOGIN);
      },
      enabled: Boolean(cookies[AUTH_TOKEN_KEY]),
    }
  );
  const { mutate: logOut } = useLogoutMutation({
    onSuccess: () => {
      queryClient.clear();
      removeCookie(AUTH_TOKEN_KEY, { domain: AUTH_TOKEN_KEY });

      navigate(PATHS.ROOT);
    },
  });

  const contextValue: AuthenticatedGuardContextType = {
    currentUser: meResult.data?.me,
    logOut: (): void => {
      logOut({});
    },
  };

  useEffect(() => {
    if (!meResult.data && !cookies[AUTH_TOKEN_KEY]) {
      navigate(PATHS.LOGIN);
      return;
    }
    if (meResult.data?.me && !meResult.data.me.isB2B) {
      window.location.replace(B2C_WEBSITE_URL);
    }
  }, [meResult.data, navigate, cookies]);

  if (meResult.data?.me.preferredLanguage && !isPreferredLanguageChanged) {
    setIsPreferredLanguageChanged(true);
    i18n.changeLanguage(meResult.data.me.preferredLanguage);
  }

  return (
    <>
      {meResult.data && (
        <AuthenticatedGuardContext.Provider value={contextValue}>
          {meResult.data.me.isB2B ? children : null}
        </AuthenticatedGuardContext.Provider>
      )}
    </>
  );
};
